import { useEffect } from "react";

export default function FaqPage() {
  useEffect(() => {
    document.title = "FAQ — ISPYFR";
  }, []);

  return (
    <div className="prose prose-sm max-w-none text-text">
      <h1 className="font-display text-2xl font-bold">Frequently Asked Questions</h1>
      <p className="text-lg text-text-secondary">
        Everything you need to know about playing, creating, and moderating
        games on ISPYFR.
      </p>

      {/* Getting Started */}
      <h2 className="mt-6 text-lg font-semibold">Getting Started</h2>

      <h3 className="mt-4 font-semibold">What is ISPYFR?</h3>
      <p>
        ISPYFR (I Spy For Real) is a real-world scavenger hunt game you play
        with friends. One person creates a game with a list of items to find,
        shares an invite code, and players compete by photographing items from
        the list in the real world. A moderator reviews submissions, and the
        player who finds the most items wins.
      </p>

      <h3 className="mt-4 font-semibold">Do I need to create an account?</h3>
      <p>
        No. ISPYFR does not require any account, email address, or password. To
        join a game, simply enter the invite code and choose a display name.
        Your display name exists only for the duration of that game and is not
        tied to any persistent identity. You can start playing in seconds.
      </p>

      <h3 className="mt-4 font-semibold">Is ISPYFR free to use?</h3>
      <p>
        Yes, ISPYFR is completely free to play. We may display occasional
        advertisements to support the platform, but there is no cost to create
        or join games. We plan to offer an optional ad-free experience in the
        future.
      </p>

      <h3 className="mt-4 font-semibold">What devices does ISPYFR work on?</h3>
      <p>
        ISPYFR is a web application that works on any device with a modern
        browser and a camera. It is optimized for mobile phones since players
        need to take photos, but it also works on tablets and desktop computers.
        No app store download is required — just open your browser and go to
        ispyfr.com.
      </p>

      {/* Gameplay */}
      <h2 className="mt-8 text-lg font-semibold">Gameplay</h2>

      <h3 className="mt-4 font-semibold">How do I join a game?</h3>
      <p>
        Tap the &ldquo;Join&rdquo; button on the home page, enter the invite
        code you received from the game creator, and choose a display name.
        You will be taken directly to the game where you can see the list of
        items to find and start playing immediately.
      </p>

      <h3 className="mt-4 font-semibold">How do I submit a photo?</h3>
      <p>
        From the game screen, tap on an item you want to submit a photo for.
        Your phone&apos;s camera will open so you can take a picture. After
        snapping the photo, confirm your submission and it will be uploaded for
        moderator review. The photo is automatically compressed before uploading
        to keep things fast.
      </p>

      <h3 className="mt-4 font-semibold">Can I resubmit if my photo is rejected?</h3>
      <p>
        Yes. If a moderator rejects your submission, you can take a new photo
        and submit again for the same item. There is no limit on resubmissions,
        so keep trying until you get it right. The moderator may include a note
        explaining why your photo was rejected to help you improve your next
        attempt.
      </p>

      <h3 className="mt-4 font-semibold">How is the winner determined?</h3>
      <p>
        The winner is the player who has the most approved submissions when the
        game ends. If the game has a time limit, it ends automatically when time
        runs out. Otherwise, the game creator can end it manually at any time.
        Final scores and standings are displayed on the results screen.
      </p>

      <h3 className="mt-4 font-semibold">Can I see what other players have found?</h3>
      <p>
        Yes. The game feed shows approved submissions from all players in real
        time, so you can track the competition and see how everyone is doing.
        This adds to the excitement as you watch other players rack up finds
        while you race to keep up.
      </p>

      {/* Creating & Moderating */}
      <h2 className="mt-8 text-lg font-semibold">Creating and Moderating</h2>

      <h3 className="mt-4 font-semibold">How do I create a game?</h3>
      <p>
        Tap &ldquo;Create&rdquo; on the home page. You will choose from
        pre-built themed item lists or create your own custom list. Configure
        any optional rules like a time limit, then share the invite code with
        your friends. The whole process takes less than a minute.
      </p>

      <h3 className="mt-4 font-semibold">What is a moderator?</h3>
      <p>
        A moderator is a player who has been given permission to review
        submissions. They can approve or reject photos submitted by other
        players. By default, the game creator is the moderator, but you can
        designate additional players as moderators if you want to share the
        responsibility or if you want to play and moderate at the same time.
      </p>

      <h3 className="mt-4 font-semibold">Can a moderator also play the game?</h3>
      <p>
        Yes. Moderators can submit photos and compete just like any other
        player. However, for the fairest experience, consider having at least
        one dedicated moderator who is not playing — especially in larger or
        more competitive games.
      </p>

      <h3 className="mt-4 font-semibold">How many items should I include?</h3>
      <p>
        It depends on how long you want the game to last and where you are
        playing. For a quick 15-to-30-minute game, five to ten items works well.
        For a longer adventure lasting an hour or more, fifteen to thirty items
        keeps things challenging. Mix easy and hard items to keep the game
        interesting for everyone.
      </p>

      {/* Privacy & Data */}
      <h2 className="mt-8 text-lg font-semibold">Privacy and Data</h2>

      <h3 className="mt-4 font-semibold">What data does ISPYFR collect?</h3>
      <p>
        ISPYFR collects only a display name (chosen by you, not your real name)
        and any photos you submit during gameplay. That is it. No email
        addresses, no phone numbers, no passwords, and no tracking cookies.
        Everything is scoped to a single game session and deleted afterward.
      </p>

      <h3 className="mt-4 font-semibold">What happens to my photos after the game?</h3>
      <p>
        All photos and game data are automatically deleted after a short
        retention window following the end of the game. This retention window
        gives players time to view results and share a recap, but after it
        closes, everything is permanently removed from our servers. We do not
        keep any photos or game data long-term.
      </p>

      <h3 className="mt-4 font-semibold">Who can see my game and photos?</h3>
      <p>
        Only players who have joined your specific game using the invite code
        can see your game&apos;s content. Games are completely private and
        invitation-only. There is no public feed, no search, and no way for
        anyone outside your game to discover or view your photos.
      </p>

      <h3 className="mt-4 font-semibold">Is ISPYFR safe for kids?</h3>
      <p>
        ISPYFR is designed as a general-audience platform and is not specifically
        directed at children under 13. However, the game&apos;s private,
        invitation-only model and minimal data collection make it suitable for
        family use. A parent or guardian can create a game and share the invite
        code only with the intended participants, ensuring a controlled
        environment. No personal information beyond a temporary display name is
        collected from any player.
      </p>

      {/* Troubleshooting */}
      <h2 className="mt-8 text-lg font-semibold">Troubleshooting</h2>

      <h3 className="mt-4 font-semibold">My camera is not working. What should I do?</h3>
      <p>
        Make sure your browser has permission to access your camera. On most
        phones, you will see a prompt asking to allow camera access when you
        first try to take a photo. If you previously denied access, you can
        re-enable it in your browser or phone settings. Try refreshing the page
        after updating permissions.
      </p>

      <h3 className="mt-4 font-semibold">I lost my invite code. Can I rejoin?</h3>
      <p>
        If you previously joined a game, it should appear on your home page
        under &ldquo;Your Games&rdquo; as long as you are using the same
        browser and have not cleared your data. If the game is no longer
        visible, ask the game creator to share the invite code again.
      </p>

      <h3 className="mt-4 font-semibold">
        The page is not loading or something looks wrong.
      </h3>
      <p>
        Try refreshing the page or clearing your browser cache. ISPYFR works
        best on modern browsers like Chrome, Safari, Firefox, or Edge. If the
        problem persists, please let us know at{" "}
        <a
          href="mailto:support@ispyfr.com"
          className="text-primary hover:underline"
        >
          support@ispyfr.com
        </a>{" "}
        and we will look into it.
      </p>
    </div>
  );
}
